import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import LinkGroupCard from '../../components/Links/LinkGroupCard';
import { PATHS } from '../../routes/paths';
import { deleteLinkGroupApi, getLinkGroupsApi, getLinkGroupItemsApi, updateLinkGroupApi } from '../../api';
import ConfirmModal from '../../components/common/ConfirmModal';

const LinkGroupManagement = () => {
  const navigate = useNavigate();

  const emptyState = useMemo(() => [], []);
  const [groups, setGroups] = useState([]);
  const [baseGroups, setBaseGroups] = useState([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const dragIdRef = useRef(null);
  const draggingRef = useRef(false);
  const suppressClickRef = useRef(false);
  const dragIndexRef = useRef(null);
  const touchStartPosRef = useRef(null);
  const touchDraggingRef = useRef(false);

  const isDirty = useMemo(() => {
    if (pendingDeleteIds.length > 0) return true;
    if (groups.length !== baseGroups.length) return true;
    // order or content diff (id order 비교)
    const a = groups.map((g) => g.id).join(',');
    const b = baseGroups.map((g) => g.id).join(',');
    return a !== b;
  }, [baseGroups, groups, pendingDeleteIds]);

  const resetToBase = () => {
    setGroups(baseGroups);
    setPendingDeleteIds([]);
  };

  const arrayMove = (arr, from, to) => {
    const next = [...arr];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
  };

  const handleTouchStart = (index, e) => {
    const t = e?.touches?.[0];
    if (!t) return;
    setDragIndex(index);
    dragIndexRef.current = index;
    touchStartPosRef.current = { x: t.clientX, y: t.clientY };
    touchDraggingRef.current = false;
  };

  const handleTouchMove = (e) => {
    const touch = e?.touches?.[0];
    if (!touch) return;

    const start = touchStartPosRef.current;
    const currentIndex = dragIndexRef.current;
    if (start == null || currentIndex == null) return;

    const dx = touch.clientX - start.x;
    const dy = touch.clientY - start.y;
    const moved = Math.abs(dx) + Math.abs(dy);

    if (!touchDraggingRef.current) {
      if (moved < 8) return;
      touchDraggingRef.current = true;
    }

    if (e.cancelable) e.preventDefault();

    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    if (!element) return;

    const row = element.closest('[data-index]');
    if (!row) return;

    const newIndex = parseInt(row.getAttribute('data-index'), 10);
    if (Number.isNaN(newIndex) || newIndex === currentIndex) return;

    setGroups((prev) => {
      const updated = [...prev];
      const [movedItem] = updated.splice(currentIndex, 1);
      updated.splice(newIndex, 0, movedItem);
      return updated;
    });

    setDragIndex(newIndex);
    dragIndexRef.current = newIndex;
  };

  const handleTouchEnd = () => {
    if (touchDraggingRef.current) {
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 120);
    }

    setDragIndex(null);
    dragIndexRef.current = null;
    touchStartPosRef.current = null;
    touchDraggingRef.current = false;
  };

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [groupsRes, itemsRes] = await Promise.all([getLinkGroupsApi(), getLinkGroupItemsApi()]);
        const g = groupsRes?.data || [];
        const items = itemsRes?.data || [];

        const byGroup = {};
        for (const it of items) {
          const gid = it.groupId;
          if (!byGroup[gid]) byGroup[gid] = [];
          byGroup[gid].push(it);
        }

        const mapped = g.map((gr) => {
          const gid = gr.groupId;
          const rawItems = (byGroup[gid] || []).slice(0, 3);
          return {
            id: gid,
            title: gr.name,
            iconName: gr.iconName,
            items: rawItems.map((it, idx) => ({
              id: it.itemId,
              rank: Number(it.sortOrder) || idx + 1,
              label: it.title,
              url: it.url,
            })),
          };
        });

        if (alive) {
          setGroups(mapped);
          setBaseGroups(mapped);
          setPendingDeleteIds([]);
        }
      } catch (e) {
        if (alive) setGroups(emptyState);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [emptyState]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveGroup = (groupId) => {
    (async () => {
      try {
        setPendingDeleteIds((prev) => (prev.includes(groupId) ? prev : [...prev, groupId]));
        setGroups((prev) => prev.filter((g) => g.id !== groupId));
      } catch (e) {
        console.error(e);
      }
    })();
  };

  const handleSave = () => {
    (async () => {
      if (saving) return;
      setSaving(true);
      try {
        if (pendingDeleteIds.length > 0) {
          await Promise.all(pendingDeleteIds.map((id) => deleteLinkGroupApi(id)));
        }

        await Promise.all(
          groups.map((g, idx) =>
            updateLinkGroupApi(g.id, { sortOrder: idx + 1 }),
          ),
        );

        const [groupsRes, itemsRes] = await Promise.all([getLinkGroupsApi(), getLinkGroupItemsApi()]);
        const g = groupsRes?.data || [];
        const items = itemsRes?.data || [];

        const byGroup = {};
        for (const it of items) {
          const gid = it.groupId;
          if (!byGroup[gid]) byGroup[gid] = [];
          byGroup[gid].push(it);
        }

        const mapped = g.map((gr) => {
          const gid = gr.groupId;
          const rawItems = (byGroup[gid] || []).slice(0, 3);
          return {
            id: gid,
            title: gr.name,
            iconName: gr.iconName,
            items: rawItems.map((it, i) => ({
              id: it.itemId,
              rank: Number(it.sortOrder) || i + 1,
              label: it.title,
              url: it.url,
            })),
          };
        });

        setGroups(mapped);
        setBaseGroups(mapped);
        setPendingDeleteIds([]);
      } catch (e) {
        console.error(e);
      } finally {
        setSaving(false);
      }
    })();
  };

  const handleAddGroup = () => {
    navigate(PATHS.adminLinkGroupItems);
  };

  const handleDone = () => {
    navigate(PATHS.links);
  };

  return (
    <Box>
      <ConfirmModal
        open={saveModalOpen}
        message="변경사항을 등록하시겠습니까?"
        confirmText="등록"
        onConfirm={() => {
          setSaveModalOpen(false);
          handleSave();
        }}
        onCancel={() => setSaveModalOpen(false)}
      />
      <Box
        sx={{
          maxWidth: 430,
          mx: 'auto',
          bgcolor: 'white',
          minHeight: '100vh',
          borderRadius: 0,
          boxShadow: '0 0 20px rgba(0,0,0,0.06)',
          px: 2,
          position: 'relative',
          pb: 10,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 60,
            borderBottom: '1px solid #e0e0e0',
            mb: 3,
            mx: -2,
            px: 2,
            position: 'relative',
          }}
        >
          <IconButton onClick={handleBack} sx={{ p: 0, position: 'absolute', left: 16 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#333' }} />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: 'center',
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            링크 모음
          </Typography>
        </Box>
        <Box
          sx={{
            width: '100%',
            maxWidth: 400,
            mx: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
          }}
        >
          {groups.map((group, index) => (
            <Box
              key={group.id}
              data-group-id={group.id}
              data-index={index}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                touchAction: dragIndex !== null ? 'none' : 'pan-y',
                opacity: dragIndex === index ? 0.85 : 1,
              }}
              draggable
              onDragStart={() => {
                dragIdRef.current = group.id;
                draggingRef.current = true;
              }}
              onDragEnd={() => {
                draggingRef.current = false;
              }}
              onTouchStart={(e) => handleTouchStart(index, e)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const fromId = dragIdRef.current;
                const toId = group.id;
                if (!fromId || fromId === toId) return;

                setGroups((prev) => {
                  const fromIdx = prev.findIndex((x) => x.id === fromId);
                  const toIdx = prev.findIndex((x) => x.id === toId);
                  if (fromIdx < 0 || toIdx < 0) return prev;
                  return arrayMove(prev, fromIdx, toIdx);
                });
              }}
              onClick={() => {
                if (draggingRef.current || suppressClickRef.current || touchDraggingRef.current) return;
                navigate(`/admin/link-group-items/${group.id}`);
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveGroup(group.id);
                }}
                sx={{
                  position: 'absolute',
                  top: -10,
                  left: -10,
                  width: 26,
                  height: 26,
                  bgcolor: '#E5E7EB',
                  border: '1px solid #D1D5DB',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                  '&:hover': { bgcolor: '#DDE1E6' },
                  zIndex: 2,
                }}
              >
                <RemoveIcon sx={{ fontSize: 18, color: '#111' }} />
              </IconButton>

              <LinkGroupCard group={group} />
            </Box>
          ))}
          <Box
            onClick={handleAddGroup}
            sx={{
              borderRadius: 3,
              border: '1px solid #EAEAEA',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              height: 176,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              bgcolor: '#fff',
              '&:hover': { bgcolor: '#fafafa' },
            }}
          >
            <AddIcon sx={{ fontSize: 28, color: '#111' }} />
          </Box>
        </Box>
        <Box
          sx={{
            position: 'fixed',
            bottom: 50,
            right: 'max(24px, calc((100vw - 430px) / 2 + 24px))',
            display: 'flex',
            gap: 1,
            zIndex: 1200,
          }}
        >
          {isDirty && (
            <Box
              onClick={resetToBase}
              sx={{
                minWidth: 72,
                height: 40,
                px: 2.5,
                py: 1,
                borderRadius: 999,
                bgcolor: '#E5E7EB',
                color: '#111',
                fontSize: 14,
                fontWeight: 600,
                boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
            >
              취소
            </Box>
          )}

          <Box
            onClick={() => {
              if (saving) return;
              if (isDirty) setSaveModalOpen(true);
              else handleDone();
            }}
            sx={{
              minWidth: 72,
              height: 40,
              px: 2.5,
              py: 1,
              borderRadius: 999,
              bgcolor: '#3679FF',
              color: '#ffffff',
              fontSize: 14,
              fontWeight: 600,
              boxShadow: '0 6px 12px rgba(0,0,0,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
              pointerEvents: saving ? 'none' : 'auto',
            }}
          >
            {isDirty ? (saving ? '저장중' : '등록') : '완료'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkGroupManagement;


